import { MonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import type { CollectionSubcategory } from '@/utils/collections/collections.types';

import { useTrackSearchSuggestionClicked } from '@/utils/amplitude/analytics/searchPage/trackSearchSuggestionClicked.analytics';
import { addImageParams } from '@/utils/urls';

type Props = CollectionSubcategory & {
    /**
     * Crop the art to fill the circle instead of letterboxing it. Opt-in, because the two
     * callers supply very different assets -- see the comment on the <img> below.
     */
    fillTile?: boolean;
    /** 1-based, so the event matches the legacy chips' `suggestedClickPosition`. */
    position: number;
};

export const CategoryCard = ({ fillTile = false, href, imageUrl, position, title }: Props) => {
    const trackSearchSuggestionClicked = useTrackSearchSuggestionClicked();
    /*
     * Request the porthole at the source assets' own resolution (they are 1100-1600px square) rather
     * than at a multiple of the render size. The tiles are a 128px floor that grows to fill the grid
     * track, so there is no single correct multiplier, and asking for the full asset keeps them sharp
     * at every width the grid produces.
     *
     * `quality` is NOT inert here even though these are .png URLs: Bunny Optimizer content-negotiates
     * WebP off the Accept header, so real browsers receive lossy WebP, where quality applies. On the
     * PNG path it genuinely is a no-op -- PNG is lossless, and q90 and q100 return identical bytes.
     *
     * No `height`: the CDN fits within the given width and preserves aspect ratio, so passing a height
     * changes neither the output nor the byte count. Verified against all six configured assets.
     */
    const formattedImageUrl = addImageParams({
        quality: 90,
        sharpen: true,
        url: imageUrl,
        width: 1100,
    });

    return (
        <MonochromeLink
            className="group flex w-full flex-col items-center gap-xs no-underline"
            href={href}
            onClick={() =>
                /*
                 * Same event and payload as the legacy related-search chips (SuggestionChip), so the
                 * curated portholes land in the existing report rather than a parallel one.
                 * `pageName` is filled in by the hook and already resolves to 'Collections Page'.
                 */
                trackSearchSuggestionClicked({
                    suggestedClickPosition: position,
                    suggestedQueryClicked: title,
                    url: href,
                })
            }
        >
            <div className="bg-gray-200 flex aspect-square w-full items-center justify-center overflow-hidden rounded-full border border-border-secondary">
                {/*
                 * The fit is the caller's choice because the two callers supply different kinds of
                 * asset, and neither fit is right for both:
                 *
                 *   - LAAC-2371 collections supply purpose-shot dist/images art. It is not square
                 *     (LAAC-2372's abstract is 1600x2044, its landscape 1599x980), so `contain`
                 *     letterboxes it against bg-gray-200 inside the bordered circle. `cover`
                 *     centre-crops to fill, and every one of those subjects survives the crop.
                 *   - The legacy star-wars page supplies auction-lot photos, and three of its six
                 *     are portrait (710x1026, 795x1100, 788x1100). `cover` would take 28-31% off
                 *     their height -- the Beckett grading header off "Signed Collectables", the
                 *     name plate off "Trading Cards". That page is frozen, so it keeps `contain`.
                 *
                 * Same split, and the same reason, as the house exclusions in buildSearchQuery:
                 * applying a LAAC-2371 decision to the live campaign page would silently change it.
                 */}
                <img
                    alt={title}
                    className={cn('h-full w-full rounded-full', fillTile ? 'object-cover' : 'object-contain')}
                    loading="eager"
                    src={formattedImageUrl}
                />
            </div>
            {/*
             * Deliberately a <p> with the label-strong classname rather than hammer-ui's
             * <LabelStrong>, which renders a <label>. This is a caption inside a link, not a
             * form control, so a <label> with no associated input would be wrong. The design
             * system prescribes exactly this: "If you need a non `label` with the same styles,
             * use the classnames `hui-text-label-strong` and `text-text-[yourColor]`."
             */}
            <p className="w-full text-center hui-text-label-strong">{title}</p>
        </MonochromeLink>
    );
};
